import { Component, OnInit, Renderer2, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { CalendarModule } from 'primeng/calendar';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClientModule } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';

import { UserService } from 'src/app/core/services/user.service';
import {
  CallUserFilter,
  User,
  UserCallHistoryData,
  UserCallHistoryParams,
  UserCallHistoryResponse,
  UserStatisticsResponse,
} from 'src/app/shared/model/user.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { Preference, PreferenceItem } from 'src/app/shared/model/preference.model';
import { domainUrl } from 'src/environments/environment';
import { RegionResponse, CountryResponse } from 'src/app/shared/model/common.model';
import { CommonService } from 'src/app/shared/services/common.service';
import { ToastModule } from 'primeng/toast';
import {
  TABLE_PARAM,
  USER_TYPES,
  STATUSES,
  WEEK_DAYS,
  PREFERENCE_ITEMS,
  GENDER_PREFERENCE,
} from 'src/app/shared/constants/common.constants';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import {
  AvailabilityDayTime,
  AvailabilityRestructuringItems,
} from 'src/app/shared/model/availability.model';
import { SidebarService } from '../../core/services/sidebar.service';

@Component({
  selector: 'app-user-details',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    HttpClientModule,
    CardModule,
    TableModule,
    ButtonModule,
    CalendarModule,
    InputSwitchModule,
    FormsModule,
    ToastModule,
  ],
  providers: [TranslateService],
  animations: [],
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss',
})
export class UserDetailsComponent implements OnInit {
  tableParams = TABLE_PARAM;

  statuses = STATUSES;

  userTypeConstants = USER_TYPES;

  baseApiUrl = domainUrl.url;

  noDataRow = [{}];

  sidebarCollapsed = false;

  filters: CallUserFilter = {
    dateRange: null,
  };

  userId: string | null = null;

  userType: string | null = null;

  userData: User | null = null;

  preferenceData: Preference | null = null;

  userStatisticsResponse: UserStatisticsResponse['data'] | null = null;

  preferences: PreferenceItem[] = [
    { label: PREFERENCE_ITEMS.GENDER, value: '' },
    { label: PREFERENCE_ITEMS.AGE_RANGE, value: '' },
    { label: PREFERENCE_ITEMS.VIDEO_CALL, value: '' },
    { label: PREFERENCE_ITEMS.ALL_RECORDINGS, value: '' },
    { label: PREFERENCE_ITEMS.COUNTRY, value: '' },
    { label: PREFERENCE_ITEMS.STATE_PROVINCE, value: '' },
    { label: PREFERENCE_ITEMS.LANGUAGE, value: '' },
    { label: PREFERENCE_ITEMS.HIDE_NAME, value: '' },
    { label: PREFERENCE_ITEMS.HIDE_IMAGE, value: '' },
  ];

  userCallHistoryData: UserCallHistoryResponse['data'] | null = null;

  userCallItems: UserCallHistoryData['items'] | [] = [];

  userCallsPageNumber: UserCallHistoryData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  userCallsPageSize: UserCallHistoryData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  first = 0;

  rows = TABLE_PARAM.PAGE_SIZE;

  sortField = 'CreatedOn';

  sortOrder = TABLE_PARAM.SORT_DEC_ORDER;

  totalRecords = 0;

  availabilityData: AvailabilityRestructuringItems[] = [];

  days: string[] = WEEK_DAYS;

  regionData!: RegionResponse['data'];

  countryData!: CountryResponse['data'];

  constructor(
    private router: Router,
    private sidebarService: SidebarService,
    private route: ActivatedRoute,
    private userService: UserService,
    private translateService: TranslateService,
    private toastService: ToasterService,
    private commonService: CommonService,
    private el: ElementRef,
    private renderer: Renderer2,
    private cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.userId = params.get('id');
      this.userType = params.get('type');

      if (this.userType !== USER_TYPES.ADMIN) {
        this.loadUserCallHistoryData(
          this.userCallsPageNumber,
          this.userCallsPageSize,
          this.sortField,
          this.sortOrder,
          this.userId,
          this.userType,
        );
      }

      if (this.userId) {
        this.fetchUserDetails(this.userId);
      }
    });
    this.sidebarService.sidebarState$.subscribe((state) => {
      this.sidebarCollapsed = state;
    });
  }

  pageChange(event: { first: number; rows: number }) {
    this.first = event.first;
    this.rows = event.rows;
    this.userCallsPageNumber = this.first / this.rows;
    this.userCallsPageSize = this.rows;
    this.loadUserCallHistoryData(
      this.userCallsPageNumber + 1,
      this.rows,
      this.sortField,
      this.sortOrder,
      this.userId,
      this.userType,
    );
  }

  sortChange(event: { field: string; order: string }) {
    this.sortField = event.field;
    this.sortOrder =
      String(event.order) === '1' ? TABLE_PARAM.SORT_ASC_ORDER : TABLE_PARAM.SORT_DEC_ORDER;
    this.loadUserCallHistoryData(
      this.userCallsPageNumber,
      this.userCallsPageSize,
      this.sortField,
      this.sortOrder,
      this.userId,
      this.userType,
    );
  }

  // Apply Filters
  applyFilters() {
    this.first = 0;
    this.userCallsPageNumber = this.userCallsPageNumber === 0 ? 1 : this.userCallsPageNumber;
    this.loadUserCallHistoryData(
      this.userCallsPageNumber,
      this.userCallsPageSize,
      this.sortField,
      this.sortOrder,
      this.userId,
      this.userType,
    );
  }

  getConvertedDate(utcDate: string) {
    return CommonService.getFormattedDate(utcDate);
  }

  loadUserCallHistoryData(
    userCallsPageNumber: number,
    userCallsPageSize: number,
    sortField: string,
    sortOrder: string,
    userId: string | null,
    userType: string | null,
  ) {
    const formattedDates =
      this.filters?.dateRange?.map((date) => CommonService.formatDate(date?.toString())) || [];

    const isValidDate = (date: string) => date && !date.includes('NaN');

    const startDate = isValidDate(formattedDates[0]) ? formattedDates[0] : '';
    const endDate = isValidDate(formattedDates[1]) ? formattedDates[1] : '';

    this.getCallHistoryByUserId(
      userCallsPageNumber,
      userCallsPageSize,
      startDate,
      endDate,
      userId,
      userType,
      sortField,
      sortOrder,
    );
  }

  // Reset Pagination on Filter
  onFilterChange() {
    this.userCallsPageNumber = 1;
    this.applyFilters();
  }

  getCallHistoryByUserId(
    pageNumber: number,
    pageSize: number,
    createdFrom: string,
    createdTo: string,
    userId: string | null,
    userType: string | null,
    sortField: string,
    sortOrder: string,
  ): void {
    const params: UserCallHistoryParams = {
      pageNumber,
      pageSize,
      createdFrom,
      createdTo,
      userId,
      userType,
      sortField,
      sortOrder,
    };
    this.userService.getCallHistoryByUserId(params).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.userCallHistoryData = response.data;
          this.userCallItems = this.userCallHistoryData?.items;
          this.totalRecords = this.userCallHistoryData.totalCount;
          // Trigger change detection to update the table
          this.cdRef.detectChanges();
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_USER_CALLS_DATA',
            ),
          );
        }
      },
    });
  }

  onCalendarOpen() {
    const calendarElement = this.el.nativeElement.querySelector(
      '.p-calendar .p-datepicker:not(.p-datepicker-inline)',
    );

    if (calendarElement) {
      const transformOrigin = window
        .getComputedStyle(calendarElement)
        .getPropertyValue('transform-origin');
      const isWithin370Range = /37[0-9]/.test(transformOrigin);

      if (isWithin370Range) {
        this.renderer.addClass(calendarElement, 'custom-transform-origin');
      } else {
        this.renderer.removeClass(calendarElement, 'custom-transform-origin');
      }
    }
  }

  formatPronouns(pronouns?: string): string {
    return pronouns ? pronouns.replace(/([a-z])([A-Z])/g, '$1/$2') : '';
  }

  fetchUserDetails(id: string) {
    this.userService.getUserDetailsById(id).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.userData = response.data;
          const userType = this.userData?.userType;
          this.getCountries(userType);
          this.fetchPreferenceDetails(this.userId, userType);
          this.fetchUserStatisticsData(this.userId, userType);

          if (userType === USER_TYPES.TALKING_BUDDY) {
            this.fetchTBAvailabilityDetails(this.userId);
          }
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant('DASHBOARD_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_USER_DATA'),
          );
        }
      },
    });
  }

  fetchPreferenceDetails(id: string | null, userType: string) {
    this.userService.getPreferenceById(id, userType).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.preferenceData = response.data;
          const countries = this.preferenceData?.countries;

          if (countries.length > 0) {
            this.getRegions(countries);
          } else {
            const regions = this.preferenceData?.regions || [];
            const languages = this.preferenceData?.languages || [];
            const genders = this.preferenceData?.genderPreference || [];
            const minAge = this.preferenceData?.minAge || '';
            const maxAge = this.preferenceData?.maxAge || '';

            this.preferences[0].value = this.getGenderPreference(genders);
            this.preferences[1].value =
              minAge === '' && maxAge === '' ? '' : `${minAge} to ${maxAge}`;
            this.preferences[2].value = this.preferenceData?.allowVideoCall === true ? 'ON' : 'OFF';
            this.preferences[3].value = this.preferenceData?.allowRecording === true ? 'ON' : 'OFF';
            this.preferences[4].value = this.getCountryNameById(countries);
            this.preferences[5].value = this.getRegionNames(regions);
            this.preferences[6].value = this.getLanguageName(languages);
            this.preferences[7].value = this.userData?.isHideName === true ? 'ON' : 'OFF';
            this.preferences[8].value = this.userData?.isHideProfileImage === true ? 'ON' : 'OFF';
          }
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_PREFERENCE_DATA',
            ),
          );
        }
      },
    });
  }

  fetchUserStatisticsData(id: string | null, userType: string) {
    this.userService.getUserStatisticsData(id, userType).subscribe({
      next: (response) => {
        if (response.status === 'Success') {
          this.userStatisticsResponse = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_USER_STATISTICS_DATA',
            ),
          );
        }
      },
    });
  }

  fetchTBAvailabilityDetails(userId: string | null) {
    this.userService.getAvailability(userId).subscribe({
      next: (response) => {
        const availability = response.data.availabilityDayTime as AvailabilityDayTime;

        // Transform availability data to the desired structure
        const maxSlots = Math.max(...Object.values(availability).map((times) => times.length));

        this.availabilityData = Array.from({ length: maxSlots }).map((_, index) => ({
          sundayTime: availability.Sunday[index]
            ? `${availability.Sunday[index].startTime} - ${availability.Sunday[index].endTime}`
            : 'N/A - N/A',
          mondayTime: availability.Monday[index]
            ? `${availability.Monday[index].startTime} - ${availability.Monday[index].endTime}`
            : 'N/A - N/A',
          tuesdayTime: availability.Tuesday[index]
            ? `${availability.Tuesday[index].startTime} - ${availability.Tuesday[index].endTime}`
            : 'N/A - N/A',
          wednesdayTime: availability.Wednesday[index]
            ? `${availability.Wednesday[index].startTime} - ${availability.Wednesday[index].endTime}`
            : 'N/A - N/A',
          thursdayTime: availability.Thursday[index]
            ? `${availability.Thursday[index].startTime} - ${availability.Thursday[index].endTime}`
            : 'N/A - N/A',
          fridayTime: availability.Friday[index]
            ? `${availability.Friday[index].startTime} - ${availability.Friday[index].endTime}`
            : 'N/A - N/A',
          saturdayTime: availability.Saturday[index]
            ? `${availability.Saturday[index].startTime} - ${availability.Saturday[index].endTime}`
            : 'N/A - N/A',
        }));
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.availabilityData = [];
          this.toastService.showErrorToast(
            this.translateService.instant(
              'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_AVAILABILITY_DATA',
            ),
          );
        }
      },
    });
  }

  getRegions(countryIds: { id: number; countryName: string }[]) {
    this.commonService.getRegions(countryIds).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.regionData = response.data;
          const countries = this.preferenceData?.countries || [];
          const regions = this.preferenceData?.regions || [];
          const languages = this.preferenceData?.languages || [];
          const genders = this.preferenceData?.genderPreference || [];
          const minAge = this.preferenceData?.minAge || '';
          const maxAge = this.preferenceData?.maxAge || '';

          if (this.regionData !== undefined) {
            this.preferences[0].value = this.getGenderPreference(genders);
            this.preferences[1].value =
              minAge === '' && maxAge === '' ? '' : `${minAge} to ${maxAge}`;
            this.preferences[2].value = this.preferenceData?.allowVideoCall === true ? 'ON' : 'OFF';
            this.preferences[3].value = this.preferenceData?.allowRecording === true ? 'ON' : 'OFF';
            this.preferences[4].value = this.getCountryNameById(countries);
            this.preferences[5].value = this.getRegionNames(regions);
            this.preferences[6].value = this.getLanguageName(languages);
            this.preferences[7].value = this.userData?.isHideName === true ? 'ON' : 'OFF';
            this.preferences[8].value = this.userData?.isHideProfileImage === true ? 'ON' : 'OFF';
          }
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_FETCH_REGION_DATA',
            ),
          );
        }
      },
    });
  }

  getCountries(userType: string) {
    this.commonService.getCountry(userType).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.countryData = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_FETCH_COUNTRY_DATA',
            ),
          );
        }
      },
    });
  }

  getCountryNameById(countryArray: { id: number; countryName: string }[]): string {
    return CommonService.joinArrayStrings(countryArray, 'countryName');
  }

  getRegionNames(regionArray: { id: number; regionName: string }[]): string {
    return CommonService.joinArrayStrings(regionArray, 'regionName');
  }

  getLanguageName(languageArray: { id: number; languageName: string }[]): string {
    return CommonService.joinArrayStrings(languageArray, 'languageName');
  }

  getGenderPreference(genderArray: { id: number; gender: string }[]): string {
    if (!genderArray || genderArray.length === 0) return '';

    const genderMap: Record<string, string> = {
      NonBinaryOther: GENDER_PREFERENCE.NON_BINARY,
    };

    const genderNames = genderArray.map((g) => genderMap[g.gender] || g.gender);

    const isAnySelected = genderNames.includes(GENDER_PREFERENCE.PREFER_NOT_TO_SAY);
    const hasOtherGenders = genderNames.some((g) => g !== GENDER_PREFERENCE.PREFER_NOT_TO_SAY);

    if (isAnySelected && !hasOtherGenders) {
      return GENDER_PREFERENCE.ANY;
    }

    if (isAnySelected && hasOtherGenders) {
      return GENDER_PREFERENCE.ANY;
    }

    return genderNames.join(', ');
  }

  redirectToCallDetailsPage(callId: string) {
    this.router.navigate(['/call-details', callId]);
  }

  updateBlockStatus(user: User | null) {
    if (!user) {
      return;
    }

    const newStatus = !user.isBlocked;
    this.userService.updateBlockStatus(user.id, newStatus).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          // eslint-disable-next-line no-param-reassign
          user.isBlocked = newStatus;
          this.toastService.showSuccessToast(
            this.translateService.instant('USER_PAGE.TOAST_MESSAGE.BLOCK_STATUS_UPDATED'),
          );
        } else {
          this.toastService.showErrorToast(
            this.translateService.instant('USER_PAGE.TOAST_MESSAGE.BLOCK_STATUS_UPDATE_FAILED'),
          );
          this.cdRef.detectChanges();
        }
      },
      error: () => {
        this.toastService.showErrorToast(
          this.translateService.instant('USER_PAGE.TOAST_MESSAGE.BLOCK_STATUS_UPDATE_FAILED'),
        );
        this.cdRef.detectChanges();
      },
    });
  }

  shouldShowPaymentProfileColumn(): boolean {
    if (!this.userData) {
      return false;
    }

    return this.userData?.userType !== this.userTypeConstants.CALLER;
  }
}
